import React, { useContext, useState, useEffect, useMemo, useRef } from 'react';
import {
  Cart,
  CartItem,
  defaultCart,
  getCartToken,
  setCartToken,
  AddItemsRequest,
  AddItemsProduct,
  AllowedUserActionItem,
  removeCartToken,
} from '@teko/cart/v2';
import { logger } from '@teko/log';
import { useAuth } from '@teko/auth-react';
import { useToast as useDesktopToast, useLoading } from '@teko/ds-desktop';
import { useToast as useMobileToast } from '@teko/ds-mobile';
import { useFeatureFlags, useScreen, useServices } from 'contexts';
import { useTenantConfig } from 'hooks';
import { ApplyPromotionParams, PriceEditorProps } from 'types';
import { t } from 'i18n';
import { useRouter } from 'next/router';
import terraKit from '@terra-js/terra-kit';

export interface CartContextStateV2 {
  // loading state for API call
  loading: boolean;
  setLoading: (loading: boolean) => void;
  // cart data returned from API
  cart: Cart;
  // all item in cart (flatten)
  flattenItems: CartItem[];

  // functions for components to use
  getCart: (params: {
    withLoading: boolean;
    removeToken?: boolean;
  }) => Promise<void>;
  onAddSingleItem: (
    body: AddItemsProduct,
    fromProductCard?: boolean,
  ) => Promise<Cart | null>;
  onAddMultipleItems: (body: AddItemsProduct[]) => Promise<void>;
  onAddComboItems: (
    body: AddItemsRequest,
    fromProductCard?: boolean,
  ) => Promise<void>;
  onRemoveItem: (item: CartItem) => Promise<void>;
  onIncrease: (item: CartItem) => Promise<void>;
  onDecrease: (item: CartItem) => Promise<void>;
  onChangeQuantity: (item: CartItem, newQuantity: number) => Promise<void>;
  onSelectItem: (item: CartItem) => Promise<void>;
  onSelectSeller: (sellerItems: CartItem[], selected: boolean) => Promise<void>;
  onDeleteCart: () => Promise<void>;
  isEmpty?: boolean;
  onChangeItemPrice: (value: PriceEditorProps) => Promise<void>;
  onApplyPromotion: (
    applyPromotionParams: ApplyPromotionParams,
  ) => Promise<void>;
  onRemovePromotion: (cartPromotionId: string) => Promise<void>;
  onApplyVoucher: (ppmCode: string) => Promise<void>;
  onRequestItemSku: (name: string, quantity?: number) => Promise<void>;
  totalCartItems: number;
}

const defaultCartContext: CartContextStateV2 = {
  loading: false,
  setLoading: () => {},
  cart: defaultCart,
  flattenItems: [],
  getCart: () => Promise.resolve(),
  onAddSingleItem: () => Promise.resolve(defaultCart),
  onAddComboItems: () => Promise.resolve(),
  onAddMultipleItems: () => Promise.resolve(),
  onRemoveItem: () => Promise.resolve(),
  onIncrease: () => Promise.resolve(),
  onDecrease: () => Promise.resolve(),
  onChangeQuantity: () => Promise.resolve(),
  onSelectItem: () => Promise.resolve(),
  onSelectSeller: () => Promise.resolve(),
  onDeleteCart: () => Promise.resolve(),
  onChangeItemPrice: () => Promise.resolve(),
  isEmpty: true,
  onApplyPromotion: () => Promise.resolve(),
  onRemovePromotion: () => Promise.resolve(),
  onApplyVoucher: () => Promise.resolve(),
  onRequestItemSku: () => Promise.resolve(),
  totalCartItems: 0,
};

const CartContext = React.createContext<CartContextStateV2 | undefined>(
  defaultCartContext,
);

export interface CartProviderPropsV2 {
  children: React.ReactNode;
}

export const CartProviderV2: ({
  children,
}: CartProviderPropsV2) => JSX.Element = ({
  children,
}: CartProviderPropsV2) => {
  const desktopToast = useDesktopToast();
  const mobileToast = useMobileToast();
  const router = useRouter();
  const [loading, setLoading] = useLoading('CartContextV2');
  const [cart, setCart] = useState<Cart>(defaultCart);
  const tokenPromise = useRef<Promise<void> | null>(null);

  const { cartToken: queryCartToken } = router.query;

  const { user, loading: authLoading } = useAuth();

  const {
    terminal,
    isMobile,
    getProcessEnv,
    partner,
    iamClientId,
    requireLogin,
  } = useTenantConfig();

  const { cartServiceV2, authService } = useServices();

  const { trackingScreenName } = useScreen();

  const { flags } = useFeatureFlags();

  const showErrorToast = (message: string) => {
    if (isMobile) {
      mobileToast.error?.({ message });
    } else {
      desktopToast.error?.({ title: message, message: null });
    }
  };

  // common handler for handling Error in most Cart functions
  const logAndThrowError = (error: unknown): unknown => {
    logger.error(error);
    showErrorToast((error as Error).message);
    throw error;
  };

  const showSuccessToast = (message: string) => {
    if (isMobile) {
      mobileToast.success?.({ message });
    } else {
      desktopToast.success?.({ title: message, message: null });
    }
  };

  // getCart when login success
  useEffect(() => {
    const fetchCart = async () => {
      const cartToken =
        terraKit.isAvailable() && flags.epic_ecom_479
          ? // use native app cart token. Ref: https://jira.teko.vn/browse/UX4BUYER-5792
            await terraKit.cart().getCartToken()
          : (queryCartToken as string);

      if (!!cartToken) {
        setCartToken(cartToken);
      }

      // loadingIAMState somehow doesn't work here
      // use localStorage instead
      const clientId =
        iamClientId || getProcessEnv('NEXT_PUBLIC_IAM_CLIENT_ID');
      const isUserInfoInLS = localStorage.getItem(`tekoid.user.${clientId}`);

      if (authLoading) return;

      if (terraKit.isAvailable() && flags.epic_ecom_479) {
        // alway get cart in embeded webview
        getCart({
          withLoading: false,
          removeToken: false,
        });
      } else if (
        user ||
        //user is not logged with partner
        (!isUserInfoInLS && !partner && !terraKit.isAvailable())
      ) {
        getCart({
          withLoading: router.pathname.includes('/cart'),
          removeToken: false,
        });
      }
    };

    fetchCart();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, queryCartToken, authLoading, terraKit.isAvailable()]);

  const flattenItems = useMemo<CartItem[]>(
    () =>
      cart.items.reduce(
        (prev, curr) => [...prev, ...curr.items],
        [] as CartItem[],
      ),
    [cart.items],
  );

  // check for cartToken, if cartToken is empty then create new one.
  const validateCartToken = async () => {
    if (!getCartToken() && !tokenPromise.current) {
      tokenPromise.current = createCart().finally(
        () => (tokenPromise.current = null),
      );
    }
    await tokenPromise.current;
  };

  // create new cart from cart service v2
  const createCart = async () => {
    try {
      const { cartToken } = await cartServiceV2.createNewCart({});
      if (cartToken) {
        setCartToken(cartToken);
      }
    } catch (error) {
      logAndThrowError(error);
    }
  };

  // get Cart from Cart Service V2
  const getCart = async ({
    withLoading = false,
    removeToken = true,
  }): Promise<void> => {
    try {
      if (!terminal) return;
      if (withLoading) setLoading(true);
      await validateCartToken();
      const cart = await cartServiceV2.getCart();
      setCart(cart);
      if (removeToken && cart?.items?.length === 0) removeCartToken();
    } catch (error) {
      if (!withLoading) {
        // without explicit loading,
        // do not show the error toast
        // since it cause confusion to users
        // in page such as condition
        logger.error(error);
        throw error;
      } else {
        logAndThrowError(error);
      }
    } finally {
      setLoading(false);
    }
  };

  const updateNativeAppCart = async (cartResponse: Cart) => {
    try {
      if (terraKit.isAvailable() && flags.epic_ecom_479 && cartResponse) {
        await terraKit.cart().updateCartSuccess(JSON.stringify(cartResponse));
      }
    } catch (e) {
      logger.error(e);
    }
  };

  const onAddToCart = async (
    body: AddItemsRequest,
    fromProductCard = false,
  ): Promise<Cart | null> => {
    // force login when add to card with tenant require login
    if (requireLogin && !user) {
      if (!authLoading) {
        authService.login();
      }
      return null;
    }

    const { groups, override } = body;

    try {
      setLoading(true);
      const { trackAddToCartManually } = await import('@teko/tracking');
      if (fromProductCard && trackingScreenName) {
        groups.forEach((group) => {
          group.products.forEach((product) => {
            trackAddToCartManually(trackingScreenName, product.sellerSku || '');
          });
        });
      }
      await validateCartToken();
      const res = await cartServiceV2.addItems({
        groups,
        override,
      });
      showSuccessToast(t('Đã thêm sản phẩm vào giỏ hàng'));
      setCart(res);
      updateNativeAppCart(res);
      return res;
    } catch (error) {
      logger.error(error);
      showErrorToast((error as Error).message);
      return null;
    } finally {
      setLoading(false);
    }
  };

  // handle user add an item to cart
  const onAddSingleItem = async (
    { sku, sellerSku, quantity, promotionId, isPrimary }: AddItemsProduct,
    fromProductCard = false,
  ): Promise<Cart | null> => {
    const body = {
      groups: [
        { products: [{ sku, sellerSku, quantity, promotionId, isPrimary }] },
      ],
      override: false,
    };
    return await onAddToCart(body, fromProductCard);
  };

  // handle user add multiple item to cart
  const onAddMultipleItems = async (
    productList: AddItemsProduct[],
  ): Promise<void> => {
    const body = { groups: [{ products: productList }], override: false };
    await onAddToCart(body, false);
  };

  // handle user add combo to cart
  const onAddComboItems = async (body: AddItemsRequest): Promise<void> => {
    await onAddToCart(body, false);
  };

  // handle when user remove an item
  const onRemoveItem = async (item: CartItem): Promise<void> => {
    const { trackRemoveFromCart } = await import('@teko/tracking');
    try {
      trackRemoveFromCart(item, cart.id);
      setLoading(true);
      await validateCartToken();
      const res = await cartServiceV2.removeItem({
        itemId: item.id,
      });
      setCart(res);
      updateNativeAppCart(res);
    } catch (error) {
      logAndThrowError(error);
    } finally {
      setLoading(false);
    }
  };

  // handle when user click add button on an item
  const onIncrease = async (item: CartItem): Promise<void> => {
    return onChangeQuantity(item, item.quantity + 1);
  };

  // handle when user click subtract button on an item
  const onDecrease = async (item: CartItem): Promise<void> => {
    return onChangeQuantity(item, item.quantity - 1);
  };

  // handle when user change quantity of an item
  const onChangeQuantity = async (
    item: CartItem,
    newQuantity: number,
  ): Promise<void> => {
    try {
      setLoading(true);
      await validateCartToken();
      const { trackChangeQuantityProduct } = await import('@teko/tracking');
      trackChangeQuantityProduct(item, newQuantity, cart.id);
      const res = await cartServiceV2.updateItemQuantity({
        itemId: item.id,
        quantity: newQuantity,
      });
      setCart(res);
      updateNativeAppCart(res);
    } catch (error) {
      logAndThrowError(error);
    } finally {
      setLoading(false);
    }
  };

  const onSelectItems = async (
    itemIdList: string[],
    isSelected: boolean,
  ): Promise<void> => {
    try {
      setLoading(true);
      await validateCartToken();
      const res = await cartServiceV2.selectItems({
        isSelected,
        itemIds: itemIdList,
      });
      setCart(res);
    } catch (error) {
      logAndThrowError(error);
    } finally {
      setLoading(false);
    }
  };

  // handle when user click checkbox of an item
  const onSelectItem = async (item: CartItem): Promise<void> => {
    const { trackSelectProduct } = await import('@teko/tracking');
    trackSelectProduct(item, !item.isSelected, cart.id);
    return onSelectItems([item.id], !item.isSelected);
  };

  // handle when user click on Checkbox of a seller
  const onSelectSeller = async (
    sellerItems: CartItem[],
    selected: boolean,
  ): Promise<void> => {
    // only toggle items which its allowedUserActions contains TOGGLE value
    const canToggleCartItems = (sellerItems || []).filter((item) =>
      (item.allowedUserActions || []).includes(AllowedUserActionItem.TOGGLE),
    );
    return onSelectItems(
      canToggleCartItems.map((item) => item.id),
      selected,
    );
  };

  // handle when user click delete all cart
  const onDeleteCart = async (): Promise<void> => {
    try {
      setLoading(true);
      await validateCartToken();
      await cartServiceV2.deleteCart();
      await getCart({ withLoading: true });
    } catch (error) {
      logAndThrowError(error);
    } finally {
      setLoading(false);
    }
  };

  const onChangeItemPrice = async (
    priceEditorData: PriceEditorProps,
  ): Promise<void> => {
    try {
      setLoading(true);
      await validateCartToken();
      const { id, editedPrice, price } = priceEditorData;
      const newCart = await cartServiceV2.changeItemPrice({
        itemId: id,
        consultationFee: Number(editedPrice) - price,
      });

      if (newCart) {
        setCart(newCart);
        showSuccessToast(t('Sửa giá sản phẩm thành công'));
      }
    } catch (error) {
      logAndThrowError(error);
    } finally {
      setLoading(false);
    }
  };

  // function to handle apply promotion
  const onApplyPromotion = async (
    applyPromotionParams: ApplyPromotionParams,
  ) => {
    const { productId, promotionCode, promotionId } = applyPromotionParams;
    try {
      setLoading(true);
      await validateCartToken();
      const res = await cartServiceV2.applyPromotion({
        ...(!!productId && { itemId: productId }),
        ppmPromotionId: promotionId,
        ppmCode: promotionCode,
      });

      showSuccessToast(t('Áp dụng khuyến mãi thành công'));
      setCart(res.data);
    } catch (error) {
      logAndThrowError(error);
    } finally {
      setLoading(false);
    }
  };

  // function to handle remove promotion
  const onRemovePromotion = async (cartPromotionId: string) => {
    try {
      setLoading(true);
      await validateCartToken();
      const req = {
        cartPromotionId,
      };

      const res = await cartServiceV2.removePromotion(req);
      showSuccessToast(t('Bỏ áp dụng khuyến mãi thành công'));
      setCart(res.data);
    } catch (error) {
      logAndThrowError(error);
    } finally {
      setLoading(false);
    }
  };

  const onApplyVoucher = async (ppmCode: string) => {
    try {
      setLoading(true);
      await validateCartToken();
      const res = await cartServiceV2.applyPromotion({
        ppmCode: ppmCode,
      });

      showSuccessToast(t('Áp dụng mã giảm giá/phiếu mua hàng thành công'));
      setCart(res.data);
    } catch (error) {
      logAndThrowError(error);
    } finally {
      setLoading(false);
    }
  };

  const onRequestItemSku = async (
    name: string,
    quantity = 1,
  ): Promise<void> => {
    // force login when add to card with tenant require login
    if (requireLogin && !user) {
      if (!authLoading) {
        authService.login();
      }
      return;
    }
    try {
      setLoading(true);
      await validateCartToken();
      const res = await cartServiceV2.requestItemSku({
        name,
        quantity,
      });
      showSuccessToast(t('Đã thêm sản phẩm vào giỏ hàng'));
      setCart(res);
    } catch (error) {
      logAndThrowError(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <CartContext.Provider
      value={{
        loading,
        setLoading,
        cart,
        flattenItems,
        getCart,
        onAddSingleItem,
        onAddMultipleItems,
        onAddComboItems,
        onRemoveItem,
        onIncrease,
        onDecrease,
        onChangeQuantity,
        onSelectItem,
        onSelectSeller,
        onDeleteCart,
        get isEmpty() {
          return cart?.items?.length === 0;
        },
        onChangeItemPrice,
        onApplyPromotion,
        onRemovePromotion,
        onApplyVoucher,
        onRequestItemSku,
        get totalCartItems() {
          return (flattenItems || []).length;
        },
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export function useCartV2(): CartContextStateV2 {
  const context = useContext<CartContextStateV2 | undefined>(CartContext);
  if (!context) {
    throw new Error(
      'useCartV2 must be inside a CartProviderV2 with a state value',
    );
  }
  return context;
}
